import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";

import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ViewProvider } from "@/context/ViewContext";

import LoginPage from "@/pages/LoginPage";
import AppShell from "@/components/AppShell";
import DashboardRouter from "@/pages/DashboardRouter";
import ClientsPage from "@/pages/ClientsPage";
import ProjectsPage from "@/pages/ProjectsPage";
import ActionsPage from "@/pages/ActionsPage";
import BuildingsPage from "@/pages/BuildingsPage";
import AsbestosWorkspace from "@/pages/asbestos/AsbestosWorkspace";
import AsbestosManagementPage from "@/pages/asbestos/AsbestosManagementPage";
import TemplatesPage from "@/pages/forms/TemplatesPage";
import FormBuilder from "@/pages/forms/FormBuilder";
import AIImportWizard from "@/pages/forms/AIImportWizard";
import InspectionsPage from "@/pages/forms/InspectionsPage";
import InductionsPage from "@/pages/inductions/InductionsPage";
import BuildInductionWizard from "@/pages/inductions/BuildInductionWizard";
import OnSitePage from "@/pages/OnSitePage";
import RichFormInstance from "@/pages/forms/RichFormInstance";
import DocumentsPage from "@/pages/documents/DocumentsPage";
import IncidentsPage from "@/pages/safety/IncidentsPage";
import SafetyCrossPage from "@/pages/safety/SafetyCrossPage";
import ResidentsPage from "@/pages/safety/ResidentsPage";
import ContactsPage from "@/pages/clients/ContactsPage";
import CertificatesPage from "@/pages/certificates/CertificatesPage";
import ClientPortalsPage from "@/pages/clients/ClientPortalsPage";
import PipelinePage from "@/pages/clients/PipelinePage";
import QuotesPage from "@/pages/clients/QuotesPage";
import SchedulePage from "@/pages/schedule/SchedulePage";
import CalendarPage from "@/pages/calendar/CalendarPage";
import BscPage from "@/pages/buildingsafety/BscPage";
import BscrWorkspace from "@/pages/buildingsafety/bscr/BscrWorkspace";
import GoldenThreadPage from "@/pages/buildingsafety/GoldenThreadPage";
import BsaRegisterPage from "@/pages/buildingsafety/BsaRegisterPage";
import FireSafetyPage from "@/pages/buildingsafety/FireSafetyPage";
import CompliancePage from "@/pages/compliance/CompliancePage";
import ReportsPage from "@/pages/reports/ReportsPage";
import PermissionsPage from "@/pages/admin/PermissionsPage";
import AssistantPage from "@/pages/ai/AssistantPage";
import ReportWriterPage from "@/pages/ai/ReportWriterPage";
import KnowledgeBasePage from "@/pages/ai/KnowledgeBasePage";
import TrainingPage from "@/pages/people/TrainingPage";
import CompetencyPage from "@/pages/people/CompetencyPage";
import ContractorsPage from "@/pages/people/ContractorsPage";
import Placeholder from "@/pages/Placeholder";
import RamsPage from "@/pages/rams/RamsPage";
import RamsTemplateBuilder from "@/pages/rams/RamsTemplateBuilder";
import CppListPage from "@/pages/cpp/CppListPage";
import CppWorkspace from "@/pages/cpp/CppWorkspace";
import WeeklyReportsPage from "@/pages/weeklyreports/WeeklyReportsPage";
import WeeklyReportEditor from "@/pages/weeklyreports/WeeklyReportEditor";
import FortnightlyReportEditor from "@/pages/weeklyreports/FortnightlyReportEditor";
import { UsersPage, AuditLogPage, SettingsPage, EmailOutboxPage } from "@/pages/AdminPages";
import PortalPresetsPage from "@/pages/admin/PortalPresetsPage";
import ProjectTemplatesPage from "@/pages/admin/ProjectTemplatesPage";
import ProjectTypesPage from "@/pages/admin/ProjectTypesPage";
import ServiceTypesPage from "@/pages/admin/ServiceTypesPage";
import FeeProposalTemplatesPage from "@/pages/admin/FeeProposalTemplatesPage";
import FolderTemplatesPage from "@/pages/admin/FolderTemplatesPage";
import SystemHealthPage from "@/pages/admin/SystemHealthPage";
import BoilerplateLibraryPage from "@/pages/admin/BoilerplateLibraryPage";
import ModuleGuard from "@/components/ModuleGuard";
import ProjectDetailPage from "@/pages/ProjectDetailPage";
import ProjectImportPage from "@/pages/projects/ProjectImportPage";
import TrackerImportPage from "@/pages/projects/TrackerImportPage";
import AnalyticsPage from "@/pages/AnalyticsPage";
import ClpListPage from "@/pages/clp/ClpListPage";
import ClpEditor from "@/pages/clp/ClpEditor";
import TmpListPage from "@/pages/tmp/TmpListPage";
import TmpEditor from "@/pages/tmp/TmpEditor";
import PublicSubmitPage from "@/pages/PublicSubmitPage";
import PublicFormPage from "@/pages/PublicFormPage";
import AttendanceSignOutPage from "@/pages/AttendanceSignOutPage";
import PulseUnsubscribePage from "@/pages/PulseUnsubscribePage";
import PublicRamsPage from "@/pages/PublicRamsPage";
import ResetPasswordPage from "@/pages/auth/ResetPasswordPage";
import ForcePasswordChange from "@/pages/auth/ForcePasswordChange";
import AccountPage from "@/pages/auth/AccountPage";
import MicrosoftCallback from "@/pages/auth/MicrosoftCallback";
import LearningPage from "@/pages/learning/LearningPage";
import LearningArticlePage from "@/pages/learning/LearningArticlePage";

const G = (id, el) => <ModuleGuard id={id}>{el}</ModuleGuard>;

function RequireAuth({ children }) {
    const { user, loading } = useAuth();
    if (loading) {
        return (
            <div className="min-h-screen grid place-items-center bg-[#F5F3F0]">
                <div className="font-serif-italic text-[#9A968F] text-xl">Loading…</div>
            </div>
        );
    }
    if (!user) return <Navigate to="/login" replace />;
    // Force the user to replace an admin-issued temp password before using the app.
    if (user.must_change_password && user.mode !== "demo") return <ForcePasswordChange />;
    return children;
}

function App() {
    return (
        <AuthProvider>
            <ViewProvider>
                <BrowserRouter>
                    <Toaster position="top-right" richColors />
                    <Routes>
                        <Route path="/" element={<Navigate to="/app/dashboard" replace />} />
                        <Route path="/login" element={<LoginPage />} />
                        <Route path="/auth/microsoft/callback" element={<MicrosoftCallback />} />
                        <Route path="/reset-password" element={<ResetPasswordPage />} />
                        <Route path="/pulse/unsubscribe" element={<PulseUnsubscribePage />} />
                        <Route path="/s/:token" element={<PublicSubmitPage />} />
                        <Route path="/f/:token" element={<PublicFormPage />} />
                        <Route path="/site-signout/:token" element={<AttendanceSignOutPage />} />
                        <Route path="/rams/s/:token" element={<PublicRamsPage />} />
                        <Route
                            path="/app"
                            element={<RequireAuth><AppShell /></RequireAuth>}
                        >
                            <Route index element={<Navigate to="dashboard" replace />} />
                            <Route path="dashboard" element={<DashboardRouter />} />
                            <Route path="account" element={<AccountPage />} />
                            <Route path="analytics" element={<AnalyticsPage />} />
                            <Route path="clients" element={<ClientsPage />} />
                            <Route path="users" element={<UsersPage />} />
                            <Route path="audit" element={<AuditLogPage />} />
                            <Route path="email-outbox" element={<EmailOutboxPage />} />
                            <Route path="settings" element={<SettingsPage />} />
                            <Route path="portal-presets" element={<PortalPresetsPage />} />
                            <Route path="project-templates" element={<ProjectTemplatesPage />} />
                            <Route path="project-types" element={<ProjectTypesPage />} />
                            <Route path="service-types" element={<ServiceTypesPage />} />
                            <Route path="fee-proposal-templates" element={<FeeProposalTemplatesPage />} />
                            <Route path="folder-templates" element={<FolderTemplatesPage />} />
                            <Route path="system-health" element={<SystemHealthPage />} />
                            <Route path="bsc-boilerplate" element={G("bsc", <BoilerplateLibraryPage />)} />
                            {/* Module placeholders for every other nav id */}
                            <Route path="buildings"   element={G("buildings", <BuildingsPage />)} />
                            <Route path="asbestos" element={G("asbestos", <AsbestosManagementPage />)} />
                            <Route path="buildings/:buildingId/asbestos" element={G("asbestos", <AsbestosWorkspace />)} />
                            <Route path="contacts"    element={G("contacts", <ContactsPage />)} />
                            <Route path="certificates" element={G("certificates", <CertificatesPage />)} />
                            <Route path="pipeline"    element={<PipelinePage />} />
                            <Route path="portals"     element={<ClientPortalsPage />} />
                            <Route path="quotes"      element={<QuotesPage />} />
                            <Route path="projects"    element={G("projects", <ProjectsPage />)} />
                            <Route path="projects/import" element={G("projects", <ProjectImportPage />)} />
                            <Route path="projects/import-tracker" element={G("projects", <TrackerImportPage />)} />
                            <Route path="projects/:id" element={G("projects", <ProjectDetailPage />)} />
                            <Route path="schedule"    element={G("schedule", <SchedulePage />)} />
                            <Route path="calendar"    element={G("calendar", <CalendarPage />)} />
                            <Route path="inspections" element={G("inspections", <InspectionsPage />)} />
                            <Route path="inspections/:id" element={G("inspections", <RichFormInstance />)} />
                            <Route path="inductions"  element={G("inductions", <InductionsPage />)} />
                            <Route path="inductions/build" element={G("inductions", <BuildInductionWizard />)} />
                            <Route path="onsite"      element={G("inductions", <OnSitePage />)} />
                            <Route path="actions"     element={G("actions", <ActionsPage />)} />
                            <Route path="incidents"   element={G("incidents", <IncidentsPage />)} />
                            <Route path="safety-cross" element={G("safety-cross", <SafetyCrossPage />)} />
                            <Route path="bsc"         element={G("bsc", <BscPage />)} />
                            <Route path="bsc/:id"     element={G("bsc", <BscrWorkspace />)} />
                            <Route path="goldenthread" element={G("goldenthread", <GoldenThreadPage />)} />
                            <Route path="bsa"         element={G("bsa", <BsaRegisterPage />)} />
                            <Route path="fire"        element={G("fire", <FireSafetyPage />)} />
                            <Route path="residents"   element={G("residents", <ResidentsPage />)} />
                            <Route path="compliance"  element={G("compliance", <CompliancePage />)} />
                            <Route path="documents"   element={G("documents", <DocumentsPage />)} />
                            <Route path="reports"     element={G("reports", <ReportsPage />)} />
                            <Route path="rams"        element={G("rams", <RamsPage />)} />
                            <Route path="rams/register" element={G("rams", <RamsPage />)} />
                            <Route path="rams/templates" element={G("rams", <RamsPage />)} />
                            <Route path="rams/templates/:id" element={G("rams", <RamsTemplateBuilder />)} />
                            <Route path="cpp"         element={G("cpp", <CppListPage />)} />
                            <Route path="cpp/:id"     element={G("cpp", <CppWorkspace />)} />
                            <Route path="weekly-reports" element={G("weekly-reports", <WeeklyReportsPage />)} />
                            <Route path="weekly-reports/fortnightly/:id" element={G("weekly-reports", <FortnightlyReportEditor />)} />
                            <Route path="weekly-reports/management-summary/:id" element={G("weekly-reports", <FortnightlyReportEditor />)} />
                            <Route path="weekly-reports/:id" element={G("weekly-reports", <WeeklyReportEditor />)} />
                            <Route path="clp"          element={G("clp", <ClpListPage />)} />
                            <Route path="clp/:id"      element={G("clp", <ClpEditor />)} />
                            <Route path="tmp"          element={G("tmp", <TmpListPage />)} />
                            <Route path="tmp/:id"      element={G("tmp", <TmpEditor />)} />
                            <Route path="templates"   element={<TemplatesPage />} />
                            <Route path="templates/ai-import" element={<AIImportWizard />} />
                            <Route path="templates/:id" element={<FormBuilder />} />
                            <Route path="training"    element={G("training", <TrainingPage />)} />
                            <Route path="competency"  element={G("competency", <CompetencyPage />)} />
                            <Route path="contractors" element={G("contractors", <ContractorsPage />)} />
                            <Route path="ai-assist"   element={<AssistantPage />} />
                            <Route path="ai-report"   element={<ReportWriterPage />} />
                            <Route path="ai-kb"       element={<KnowledgeBasePage />} />
                            <Route path="permissions" element={<PermissionsPage />} />
                            <Route path="learning"    element={G("learning", <LearningPage />)} />
                            <Route path="learning/:id" element={G("learning", <LearningArticlePage />)} />
                        </Route>
                        <Route path="*" element={<Navigate to="/app/dashboard" replace />} />
                    </Routes>
                </BrowserRouter>
            </ViewProvider>
        </AuthProvider>
    );
}

export default App;
